import React, { memo } from 'react'
import { Link } from 'gatsby'
import resolveLink from '@/utils/resolveLink.js'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondary_blue'
  | 'secondary_green'
  | 'tertiary'
  | 'tertiary_blue'
  | 'tertiary_green'

type ButtonBase = 'default' | 'light' | 'dark'
type ButtonSize = 'default' | 'xs'
type ButtonTarget = '_blank' | '_self' | '_parent' | '_top'

interface ButtonProps {
  children: React.ReactNode
  className?: string
  to?: string
  target?: ButtonTarget
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  activeClassName?: string
  variant?: ButtonVariant
  base?: ButtonBase
  before?: React.HTMLAttributes<HTMLDivElement>
  size?: ButtonSize
  title?: string
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-red-500 text-white hover:bg-gradient-to-l hover:from-red-600 hover:to-orange-500 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed',
  secondary:
    'bg-white text-[#002432] shadow-md hover:bg-gradient-to-l hover:from-gray-300 hover:to-white hover:shadow-md disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none disabled:cursor-not-allowed',
  secondary_blue:
    'bg-slate-900 text-white hover:bg-gradient-to-l hover:from-slate-900 hover:to-slate-700 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed',
  secondary_green:
    'bg-emerald-400 text-[#002432] hover:bg-gradient-to-l hover:from-emerald-400 hover:to-emerald-300 hover:shadow-lg disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed',
  tertiary:
    'border-2 border-white text-[#002432] hover:bg-white hover:text-black hover:shadow-md disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed',
  tertiary_blue:
    'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed',
  tertiary_green:
    'border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-slate-900 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed',
} as const

const SIZE_STYLES: Record<ButtonSize, string> = {
  default: 'md:px-7 md:py-3.5 md:text-sm md:font-semibold',
  xs: 'px-7 py-3.5 text-sm font-semibold',
} as const

const MOBILE_STYLES = 'px-7 py-3.5 text-sm font-semibold'

const BASE_CLASSES =
  'font-display inline-block rounded-full transition-all duration-500'

export const Button = memo(
  ({
    children,
    className = '',
    to,
    target,
    onClick,
    activeClassName = '',
    variant = 'primary',
    size = 'default',
    before,
    title = '',
    disabled = false,
    type = 'button',
  }: ButtonProps) => {
    if (!children) return null

    const variantClass = VARIANT_STYLES[variant]
    const sizeClass = SIZE_STYLES[size]
    const classes =
      `${MOBILE_STYLES} ${sizeClass} ${BASE_CLASSES} ${variantClass} ${className}`.trim()
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        e.preventDefault()
        return
      }
      onClick?.(e)
    }

    // Handle button case
    if (!to) {
      return (
        <button
          className={classes}
          data-before={before}
          type={type}
          title={title}
          disabled={disabled}
          onClick={handleClick}
        >
          {children}
        </button>
      )
    }

    // Handle external links and special cases
    if (
      typeof to === 'string' &&
      (to.startsWith('tel:') || to.startsWith('mailto:') || target === '_blank')
    ) {
      const hrefValue = disabled ? undefined : to

      return (
        <a
          className={classes}
          href={hrefValue}
          target={target}
          data-before={before}
          title={title}
          onClick={e => {
            if (disabled) {
              e.preventDefault()
              return
            }
            onClick?.(e)
          }}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      )
    }

    // Handle internal Gatsby links
    const linkTo = disabled ? '#' : resolveLink(to)

    return (
      <Link
        className={classes}
        to={linkTo}
        activeClassName={activeClassName}
        onClick={e => {
          if (disabled) {
            e.preventDefault()
            return
          }
          onClick?.(e)
        }}
        data-before={before}
        title={title}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
      >
        {children}
      </Link>
    )
  },
)

Button.displayName = 'Button'
